import { Link } from 'react-router-dom';
import { useSeo } from '@/hooks/useSeo';

const principles = [
  { icon: 'ri-calendar-check-line', title: '每日准时', desc: '坚持每日整理更新动态，让今日更新信息可靠、及时、可依赖。' },
  { icon: 'ri-links-line', title: '进度清晰', desc: '汉化进度与连载状态分类标注，最新章节标题一目了然，追更有据。' },
  { icon: 'ri-shield-check-line', title: '尊重原作', desc: '尊重原作者与汉化组劳动，仅做资讯整理与进度追踪，支持正版创作。' },
  { icon: 'ri-user-heart-line', title: '读者优先', desc: '所有栏目与分类围绕读者追更习惯设计，让信息快速触达最需要的人。' },
];

const milestones = [
  { year: '2023', title: '缘起', desc: '从一群同人爱好者的日常分享群起步，整理更新信息的需求越来越旺盛。' },
  { year: '2024', title: '建站', desc: '今日同人库正式上线，确立每日更新速递与汉化连载追踪两大核心栏目。' },
  { year: '2025', title: '成长', desc: '引入六大主题分类与追更排行，覆盖更多类型同人作品。' },
  { year: '2026', title: '同行', desc: '坚持每日整理，服务越来越多追更读者，成为同人更新信息的一站式入口。' },
];

export default function AboutPage() {
  useSeo({
    title: '关于我们 - 今日同人库品牌介绍与内容理念',
    description:
      '了解今日同人库的网站定位、发展理念与内容原则。作为每日整理同人作品更新动态的资讯站点，我们坚持准时、清晰、尊重与读者优先。',
    keywords: '今日同人库,关于我们,网站定位,内容原则,同人资讯',
  });

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于今日同人库',
    inLanguage: 'zh-CN',
    description: '每日整理同人作品更新动态、最新汉化与新作连载的资讯站点。',
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <div className="bg-secondary-50/70 px-4 md:px-6 py-10 md:py-14">
        <div className="max-w-4xl mx-auto">
          <nav className="text-sm text-foreground-500 mb-3" aria-label="面包屑">
            <Link to="/" className="hover:text-primary-700">首页</Link>
            <span className="mx-2">/</span>
            <span className="text-foreground-700">关于我们</span>
          </nav>
          <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">关于今日同人库</h1>
          <p className="mt-3 text-foreground-500 text-sm md:text-base max-w-2xl leading-relaxed">
            今日同人库，一个为同人作品读者而生的每日更新追踪站点。我们把散落在各处的更新信息整理到一起，让你打开这里，就能掌握今天值得追的一切。
          </p>
        </div>
      </div>

      <div className="px-4 md:px-6 max-w-4xl mx-auto py-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {principles.map((p) => (
            <div key={p.title} className="bg-background-50 border border-background-200 rounded-lg p-6">
              <span className="w-11 h-11 flex items-center justify-center rounded-lg bg-primary-100 text-primary-700 text-xl">
                <i className={p.icon}></i>
              </span>
              <h2 className="mt-4 font-heading text-lg font-semibold text-foreground-950">{p.title}</h2>
              <p className="mt-2 text-sm text-foreground-500 leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>

        <div className="mt-16">
          <h2 className="font-heading text-2xl font-bold text-foreground-950 mb-2">发展历程</h2>
          <p className="text-foreground-500 text-sm mb-8">从一个小小的分享群，到今日同人库的今天。</p>
          <div className="space-y-0">
            {milestones.map((m, i) => (
              <div key={m.year} className="relative flex gap-5 pb-8 last:pb-0">
                <div className="flex flex-col items-center">
                  <span className="w-3 h-3 rounded-full bg-accent-500 ring-4 ring-accent-100 mt-1.5"></span>
                  {i < milestones.length - 1 && <span className="w-px flex-1 bg-background-200"></span>}
                </div>
                <div className="pb-2">
                  <p className="font-heading text-lg font-bold text-primary-700">{m.year}</p>
                  <h3 className="mt-1 font-semibold text-foreground-900">{m.title}</h3>
                  <p className="mt-1 text-sm text-foreground-500 leading-relaxed">{m.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-16">
          <h2 className="font-heading text-2xl font-bold text-foreground-950 mb-6">读者口碑</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { n: '9.8万+', d: '累计更新阅读' },
              { n: '480+', d: '持续追踪作品' },
              { n: '4.9/5', d: '读者满意度' },
            ].map((s) => (
              <div key={s.n} className="bg-background-100 rounded-lg p-6 text-center">
                <p className="font-heading text-2xl md:text-3xl font-bold text-primary-700">{s.n}</p>
                <p className="mt-2 text-sm text-foreground-500">{s.d}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-16 bg-primary-50 border border-primary-100 rounded-lg p-6 md:p-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div>
            <h2 className="font-heading text-lg font-semibold text-foreground-950">想与我们合作或交流？</h2>
            <p className="mt-1 text-sm text-foreground-500">无论是汉化组投稿还是读者建议，我们都很乐意倾听。</p>
          </div>
          <Link
            to="/contact"
            className="px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            联系我们
          </Link>
        </div>
      </div>
    </>
  );
}